import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import { Form, Formik, FormikHelpers } from 'formik';

import { AxiosError } from 'axios';

import { Button } from 'components/Button';
import NavigationBlocker from 'components/NavigationBlocker';

import { StyledTextField } from 'components/Input/styles';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import API from 'utils/api';

import { UpdatedUserPayload } from 'utils/events/types';
import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import { EditUserResponse, UserCustomFieldValue } from 'utils/api/modules/user/user.types';
import { getErrorMessage, isBadRequest } from 'utils/api/errors';

import Logger from 'utils/logger';

import { useStore } from 'utils/hooks/store';

import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import useMappedFields from '../../hooks/useMappedFields';
import { customFieldName, UnresolvedValue } from '../IdpManaged';
import { StyledRowTitle, RowValue, TabTitle } from '../PersonalInformation/styles';
import { StyledSectionGrid, StyledRowTitle as EditRowTitle, ButtonWrapper } from '../EditPersonalInformation/styles';
import { CardHeaderRow } from './styles';

const NOT_SET = 'Not set';

interface CustomFieldsFormValues {
  fields: { slot: number; value: string }[];
}

const CustomFieldsEdit: React.FC<{
  customFields: UserCustomFieldValue[];
  setEditing: (val: boolean) => void;
}> = ({ customFields, setEditing }) => {
  const {
    userDetails: { details },
    notification,
  } = useStore();
  const { isMapped } = useMappedFields();

  if (!details) {
    return null;
  }

  const initialValues: CustomFieldsFormValues = {
    fields: customFields.map(({ slot, value }) => ({ slot, value })),
  };

  const onUpdate = async (
    data: CustomFieldsFormValues,
    helpers: FormikHelpers<CustomFieldsFormValues>
  ): Promise<void> => {
    try {
      helpers.setSubmitting(true);
      const payload = { attributes: { customFields: data.fields } };
      const response = await API.user.updateUserData(details.uuid, payload as unknown as Partial<EditUserResponse>)();
      const updated: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, updated);
      notification.enqueueSuccessSnackbar('User details updated successfully!');
      setEditing(false);
    } catch (e) {
      const error = e as AxiosError;
      notification.enqueueErrorSnackbar(getErrorMessage(error, 'Could not update user details'));
      if (!isBadRequest(error)) {
        Logger.error('Invalid update custom fields API response', e);
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={(values, helpers) => onUpdate(values, helpers)}>
      {({ values, handleChange, isSubmitting, dirty }) => (
        <Form>
          <NavigationBlocker
            when={dirty}
            shouldBlockNavigation={() => dirty}
            navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
          />
          <Grid container sx={{ padding: '20px' }}>
            {values.fields.map((field, index) => (
              <React.Fragment key={field.slot}>
                <StyledSectionGrid item container xs={12} alignContent="flex-start" marginTop="16px">
                  <EditRowTitle $disabled={false}>{customFields[index].label}</EditRowTitle>
                </StyledSectionGrid>
                <StyledSectionGrid item container xs={12}>
                  <StyledTextField
                    fullWidth
                    id={`fields.${index}.value`}
                    name={`fields.${index}.value`}
                    value={field.value}
                    disabled={isSubmitting || isMapped(customFieldName(field.slot))}
                    onChange={handleChange}
                  />
                </StyledSectionGrid>
              </React.Fragment>
            ))}
            <ButtonWrapper>
              <Button variant="outlined" onClick={() => setEditing(false)} size="large">
                Cancel
              </Button>
              <Button variant="contained" type="submit" size="large" disabled={isSubmitting}>
                Save
              </Button>
            </ButtonWrapper>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export const UserCustomFields: React.FunctionComponent = () => {
  const {
    userDetails: { details },
    session,
  } = useStore();

  const [editing, setEditing] = React.useState(false);
  const { isMapped, unresolvedFor } = useMappedFields();

  if (!details) {
    return null;
  }

  const customFields = details.attributes?.customFields ?? [];
  const canEdit = !!session.user && ADMIN_AND_ACCOUNT_MANAGERS.includes(session.user.role);

  return (
    <>
      <CardHeaderRow>
        <TabTitle>Custom fields</TabTitle>
        {!editing && canEdit && (
          <Button size="medium" variant="text" onClick={() => setEditing(true)} disabled={!details.isActive}>
            <EditIcon sx={{ width: 20, height: 20, marginRight: '8px' }} />
            Edit
          </Button>
        )}
      </CardHeaderRow>
      {editing ? (
        <CustomFieldsEdit customFields={customFields} setEditing={setEditing} />
      ) : (
        <Grid container marginTop="16px" sx={{ padding: '0px 20px 20px 20px' }} alignItems="center">
          {customFields.map((field) => (
            <React.Fragment key={field.slot}>
              <SectionGrid item container xs={2} alignContent="flex-start" margin="8px 0">
                <StyledRowTitle>{field.label}</StyledRowTitle>
              </SectionGrid>
              <SectionGrid item container xs={10}>
                <RowValue>{field.value || NOT_SET}</RowValue>
                <UnresolvedValue
                  field={customFieldName(field.slot)}
                  unresolved={unresolvedFor(customFieldName(field.slot))}
                />
              </SectionGrid>
            </React.Fragment>
          ))}
        </Grid>
      )}
    </>
  );
};

export default observer(UserCustomFields);
